<template>
  <div class="overflow-auto">
    <b-table
      id="sim-table"
      striped
      fixed
      small
      responsive="sm"
      :items="items"
      :fields="fields"
      label-sort-asc=""
      label-sort-desc=""
      label-sort-clear=""
    >
      <template #cell(Dose)="{ item }"
        >{{ item.Dose }} <span v-html="displaySIUnits(item.doseUnit)"></span
      ></template>
      <template #cell(Interval)="{ item }"
        >{{ item.Interval }} <span v-html="displaySIUnits(item.intervalUnit)"></span
      ></template>
      <template #cell(Infusion)="{ item }"
        >{{ item.Infusion }} <span v-html="displaySIUnits(item.InfusionUnit)"></span
      ></template>
      <template #cell(AUC)="{ item }"
        >{{ item.AUC }} <span v-html="displaySIUnits(item.aucUnit)"></span
      ></template>
      <template #cell(TMIC)="{ item }">{{ item.TMIC }} %</template>
      <template #cell(Peak)="{ item }"
        >{{ item.Peak }} <span v-html="displaySIUnits(item.peakUnit)"></span
      ></template>
      <template #cell(Trough)="{ item }"
        >{{ item.Trough }} <span v-html="displaySIUnits(item.troughUnit)"></span
      ></template>
      <template #cell(SS)="{ item }"
        >{{ item.SS }} <span v-html="displaySIUnits(item.ssUnit)"></span
      ></template>
      <template #cell(Rate)="{ item }"
        >{{ item.Rate }} <span v-html="displaySIUnits(item.rateUnit)"></span
      ></template>
      <template #cell(Calculation)="{ item }">{{ item.Calculation }}</template>
      <template #cell(Action)="{ item }">
        <b-button title="Select Plan" :variant="buttonClass(item)" @click="selectItem(item)"
          >select<span v-if="buttonClass(item) === 'success'" class="text-white">ed</span></b-button
        >
      </template>
    </b-table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { displaySIUnits } from '@/models/utils/constants';
import { TargetType } from '@/models/Enums';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'SimulationDoses' });

const props = defineProps<{
  data: any[];
  method?: string;
  showCalculation?: boolean;
}>();

const store = useDosingStore();

const selectedPlan = computed(() => store.getSelectedPlan);
const items = computed(() => props.data);

const fields = computed(() => {
  const model = store.getModel;
  const target = store.getTarget;
  const method = props.method ?? 'IV';
  const isFtTarget = target?.target === TargetType.FT;
  const base =
    method === 'CONT'
      ? [
          { key: 'Dose', label: 'Dose / 24 Hours', sortable: true },
          { key: 'SS', label: 'Steady state', sortable: true },
          { key: 'AUC', label: 'AUC24', sortable: true },
          { key: 'Action', sortable: false },
        ]
      : model?.getCompartment() === 2 && isFtTarget
        ? [
            { key: 'Dose', sortable: true },
            { key: 'Infusion', sortable: true },
            { key: 'Interval', sortable: true },
            { key: 'TMIC', label: 'fT>MIC', sortable: true },
            { key: 'Peak', sortable: true },
            { key: 'Action', sortable: false },
          ]
        : [
            { key: 'Dose', sortable: true },
            { key: 'Infusion', sortable: true },
            { key: 'Interval', sortable: true },
            { key: 'AUC', label: 'AUC24', sortable: true },
            { key: 'Peak', sortable: true },
            { key: 'Trough', sortable: true },
            { key: 'Action', sortable: false },
          ];
  if (props.showCalculation) {
    base.push({ key: 'Calculation', sortable: false });
  }
  return base;
});

function selectItem(item: any) {
  store.saveSelectedPlan(item.plan);
}

function buttonClass(item: any) {
  return item.plan.id === selectedPlan.value?.id ? 'success' : 'outline-primary';
}
</script>

<style scoped></style>
